/* eslint-disable */
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useHistory } from "react-router-dom";
import {
  message,
  Table,
  Button,
  Input,
  Tag,
  Modal,
  Alert,
  Dropdown,
  Spin,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { tripsApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";
import ResponsiveTable from "../../_components/ResponsiveTable";

const { Search } = Input;

// ─────────────────────────────────────────────────────────────────────────────
const TripsList = () => {
  const history = useHistory();

  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [trips, setTrips] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const [filters, setFilters] = useState({ page: 1, limit: 10 });
  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    id: null,
    name: "",
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hiddenTripIds, setHiddenTripIds] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);

  // Load user permissions
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr) {
          setUserPermissions([]);
          setIsCheckingPermission(false);
          return;
        }

        const userData = JSON.parse(userStr);

        // Extract permissions - handle both string array and object array
        let permissions = [];

        if (userData.permissions && Array.isArray(userData.permissions)) {
          permissions = userData.permissions;
        } else if (userData.role && userData.role.permissions) {
          permissions = userData.role.permissions;
        }

        // Normalize to strings
        const normalizedPermissions = permissions.map((p) =>
          typeof p === "string" ? p : p.name,
        );

        setUserPermissions(normalizedPermissions);
        console.log("TripsList - User permissions:", normalizedPermissions);
      } catch (error) {
        console.error("Error loading user permissions:", error);
        setUserPermissions([]);
      } finally {
        setIsCheckingPermission(false);
      }
    };

    loadUserPermissions();
  }, []);

  // Check permissions using the standardized hasPermission utility
  const canViewTrips = useMemo(() => {
    return hasPermission(userPermissions, "trips.view");
  }, [userPermissions]);

  const canCreateTrips = useMemo(() => {
    return hasPermission(userPermissions, "trips.create");
  }, [userPermissions]);

  const canEditTrips = useMemo(() => {
    return hasPermission(userPermissions, "trips.edit");
  }, [userPermissions]);

  const canDeleteTrips = useMemo(() => {
    return hasPermission(userPermissions, "trips.delete");
  }, [userPermissions]);

  // Combined manage permission for backward compatibility
  const canManageTrips = useMemo(() => {
    return canCreateTrips || canEditTrips || canDeleteTrips;
  }, [canCreateTrips, canEditTrips, canDeleteTrips]);

  useEffect(() => {
    if (!isCheckingPermission && canViewTrips) {
      fetchTrips();
    }
  }, [filters, debouncedSearch, canViewTrips, isCheckingPermission]);

  const fetchTrips = async () => {
    try {
      setLoading(true);
      console.log("🔍 Fetching trips with query:", {
        page: filters.page,
        limit: filters.limit,
        search: debouncedSearch,
      });

      const query = {
        page: filters.page,
        limit: filters.limit,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      };

      const response = await tripsApi.getAll(query);
      console.log("📊 API Response:", {
        total: response.total,
        dataLength: (response.data || []).length,
        hasSearch: !!debouncedSearch,
      });

      let allTrips = response.data || [];

      // Client-side fallback filtering
      if (debouncedSearch) {
        const searchLower = debouncedSearch.toLowerCase();
        allTrips = allTrips.filter(
          (trip) =>
            trip.name?.toLowerCase().includes(searchLower) ||
            trip.route?.toLowerCase().includes(searchLower) ||
            trip.description?.toLowerCase().includes(searchLower),
        );
        console.log(
          `🔍 Client-side filtered from ${response.data?.length || 0} to ${
            allTrips.length
          }`,
        );
      }

      const visibleTrips = allTrips.filter(
        (trip) => trip.status === "active" && !hiddenTripIds.includes(trip.id),
      );

      setTrips(visibleTrips);

      // Adjust total for client-side filtering and hidden items
      let adjustedTotal = response.total || allTrips.length;
      if (debouncedSearch) {
        adjustedTotal = allTrips.length; // Use client-side filtered count
      }
      adjustedTotal = Math.max(0, adjustedTotal - hiddenTripIds.length);
      setTotalItems(adjustedTotal);

      setSearching(false);
    } catch (error) {
      console.error(
        "❌ Error fetching trips:",
        error.response?.data || error.message,
      );
      if (error.response?.status === 403) {
        message.error("You don't have permission to view trips");
      } else {
        const errorMsg =
          error.response?.data?.message ||
          error.message ||
          "Failed to load trips";
        message.error(`Search error: ${errorMsg}`);
      }
      setSearching(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback((value) => {
    setSearch(value);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setSearching(true);
    }, 300);
  }, []);

  const handleDelete = async () => {
    if (!canDeleteTrips) {
      message.error("You don't have permission to delete trips");
      setDeleteModal({ visible: false, id: null, name: "" });
      return;
    }

    try {
      setDeleteLoading(true);
      await tripsApi.remove(deleteModal.id);
      setHiddenTripIds((ids) =>
        ids.includes(deleteModal.id) ? ids : [...ids, deleteModal.id],
      );
      const nextTrips = trips.filter((t) => t.id !== deleteModal.id);
      setTrips(nextTrips);
      setTotalItems((c) => Math.max(0, c - 1));
      message.success("Trip deleted");
      setDeleteModal({ visible: false, id: null, name: "" });
      if (!nextTrips.length && filters.page > 1)
        setFilters((f) => ({ ...f, page: f.page - 1 }));
    } catch (e) {
      if (e.response?.status === 403) {
        message.error("You don't have permission to delete this trip");
      } else {
        message.error(e.response?.data?.message || "Failed to delete trip");
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      title: "Trip Name",
      dataIndex: "name",
      key: "name",
      render: (name, r) => (
        <div>
          <strong>{name}</strong>
          <div className="small text-muted">
            {r.startDate} → {r.endDate}
          </div>
        </div>
      ),
    },
    {
      title: "Time",
      key: "time",
      render: (_, r) => `${r.startTime} – ${r.endTime}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag color={s === "active" ? "green" : "default"}>
          {s?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Decks",
      dataIndex: "tripDecks",
      key: "decks",
      render: (decks) => (
        <span>
          {(decks || []).map((d) => (
            <Tag key={d.id}>{d.deck?.name}</Tag>
          ))}
        </span>
      ),
    },
    // Actions column - only show if user has any management permissions
    ...(canManageTrips
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 80,
            render: (_, record) => {
              const items = [];

              // View is always available if user can view trips
              if (canViewTrips) {
                items.push({
                  key: "view",
                  label: "View Details",
                  icon: <EyeOutlined />,
                  onClick: () => history.push(`/trips/${record.id}`),
                });
              }

              // Edit - only if user has edit permission
              if (canEditTrips) {
                items.push({
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: () => history.push(`/trips/${record.id}/edit`),
                });
              }

              // Schedules - view schedules for this trip
              // if (canViewTrips) {
              //   items.push({
              //     key: "schedules",
              //     label: "Schedules",
              //     icon: <EyeOutlined />,
              //     onClick: () => history.push(`/schedules?tripId=${record.id}`),
              //   });
              // }

              // Delete - only if user has delete permission
              if (canDeleteTrips) {
                if (items.length > 0) {
                  items.push({ type: "divider" });
                }
                items.push({
                  key: "delete",
                  label: "Delete",
                  icon: <DeleteOutlined />,
                  onClick: () =>
                    setDeleteModal({
                      visible: true,
                      id: record.id,
                      name: record.name,
                    }),
                  danger: true,
                });
              }

              if (items.length === 0) return null;

              return (
                <Dropdown
                  menu={{ items }}
                  placement="bottomRight"
                  trigger={["click"]}
                >
                  <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
              );
            },
          },
        ]
      : []),
  ];

  // Show loading while checking permissions
  if (isCheckingPermission) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div className="text-center py-5">
              <Spin size="large" />
              <p className="mt-2">Checking permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Permission check for viewing the page
  if (!canViewTrips) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to view trips.</p>
                  <p className="mt-2">
                    <strong>Required permission:</strong> trips.view
                  </p>
                  <p className="mt-2">
                    <strong>Your permissions:</strong>{" "}
                    {userPermissions.length > 0
                      ? userPermissions.join(", ")
                      : "None"}
                  </p>
                </div>
              }
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header d-flex justify-content-between align-items-center">
                  <div>
                    <h5>Trip Management</h5>
                    <p className="text-muted mb-0 mt-1">
                      {canManageTrips
                        ? `You have ${canCreateTrips ? "create, " : ""}${
                            canEditTrips ? "edit, " : ""
                          }${
                            canDeleteTrips ? "delete" : ""
                          } access to manage trips`
                        : "You can view trips but cannot modify them"}
                    </p>
                  </div>
                  {/* Add Trip button - only visible to users with create permission */}
                  {canCreateTrips && (
                    <div className="list-btn">
                      <Button
                        type="primary"
                        style={{
                          backgroundColor: "#7B4D12",
                          borderColor: "#7B4D12",
                        }}
                        icon={<PlusOutlined />}
                        onClick={() => history.push("/trips/add")}
                      >
                        Add Trip
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* View Only Mode Alert */}
              {!canManageTrips && canViewTrips && (
                <Alert
                  message="View Only Mode"
                  description="You have view-only access to trips. To create, edit, or delete trips, please contact an administrator."
                  type="info"
                  showIcon
                  className="mb-4"
                />
              )}

              <div className="row mb-4">
                <div className="col-md-4">
                  <Search
                    placeholder={searching ? "Searching..." : "Search trips…"}
                    onSearch={handleSearch}
                    enterButton={<SearchOutlined />}
                    loading={searching}
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              {trips.length === 0 && !loading ? (
                <div className="text-center py-5">
                  <p className="text-muted">No trips found</p>
                  {canCreateTrips && (
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                      icon={<PlusOutlined />}
                      onClick={() => history.push("/trips/add")}
                    >
                      Create your first trip
                    </Button>
                  )}
                </div>
              ) : (
                <ResponsiveTable
                  columns={columns}
                  dataSource={trips}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    current: filters.page,
                    pageSize: filters.limit,
                    total: totalItems,
                    showSizeChanger: true,
                    showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}`,
                  }}
                  onChange={(p) =>
                    setFilters((f) => ({
                      ...f,
                      page: p.current,
                      limit: p.pageSize,
                    }))
                  }
                  scroll={{ x: true }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal - only shown to users with delete permission */}
      {canDeleteTrips && (
        <Modal
          title={
            <span>
              <DeleteOutlined style={{ marginRight: 8, color: "#dc2626" }} />
              Delete Trip
            </span>
          }
          open={deleteModal.visible}
          onOk={handleDelete}
          onCancel={() =>
            setDeleteModal({ visible: false, id: null, name: "" })
          }
          confirmLoading={deleteLoading}
          okText="Yes, Delete"
          okType="danger"
          width={420}
        >
          <Alert
            message="This trip will be removed and will no longer accept new bookings."
            type="warning"
            showIcon
            className="mb-3"
          />
          <p>
            Are you sure you want to delete <strong>{deleteModal.name}</strong>?
          </p>
          <Alert
            message="This action cannot be undone."
            type="error"
            showIcon
            className="mt-2"
          />
        </Modal>
      )}
    </div>
  );
};

export default TripsList;
